import { useContext, useMemo } from 'react';
import cx from 'classnames';
import { Group } from '@visx/group';
import { Text, useText } from '@visx/text';
import useMeasure from 'react-use-measure';
import AnnotationContext from "../context/AnnotationContext.js";
import AnchorLine from "./LabelAnchorLine.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_PADDING = {
  top: 12,
  right: 12,
  bottom: 12,
  left: 12
};
function getCompletePadding(padding) {
  if (typeof padding === 'undefined') return DEFAULT_PADDING;
  if (typeof padding === 'number') {
    return {
      top: padding,
      right: padding,
      bottom: padding,
      left: padding
    };
  }
  return {
    ...DEFAULT_PADDING,
    ...padding
  };
}
export default function Label(_ref) {
  let {
    anchorLineStroke = '#222',
    backgroundFill = '#eaeaea',
    backgroundPadding,
    backgroundProps,
    className,
    fontColor = '#222',
    horizontalAnchor: propsHorizontalAnchor,
    resizeObserverPolyfill,
    showAnchorLine = true,
    showBackground = true,
    subtitle,
    subtitleDy = 4,
    subtitleFontSize = 12,
    subtitleFontWeight = 200,
    subtitleProps,
    title,
    titleFontSize = 16,
    titleFontWeight = 600,
    titleProps,
    verticalAnchor: propsVerticalAnchor,
    width: propWidth,
    maxWidth = 125,
    x: propsX,
    y: propsY
  } = _ref;
  // we must measure the rendered html content to compute container height
  const [titleRef, titleBounds] = useMeasure({
    polyfill: resizeObserverPolyfill
  });
  const [subtitleRef, subtitleBounds] = useMeasure({
    polyfill: resizeObserverPolyfill
  });
  const padding = useMemo(() => getCompletePadding(backgroundPadding), [backgroundPadding]);

  // if props are provided, they take precedence over context
  const {
    x = 0,
    y = 0,
    dx = 0,
    dy = 0
  } = useContext(AnnotationContext);
  const height = Math.floor(padding.top + padding.bottom + (titleBounds.height ?? 0) + (subtitleBounds.height ?? 0));
  const {
    wordsByLines: titleWordsByLine
  } = useText({
    children: title,
    verticalAnchor: 'start',
    capHeight: titleFontSize,
    fontSize: titleFontSize,
    fontWeight: titleFontWeight,
    fontFamily: titleProps?.fontFamily,
    width: maxWidth,
    ...titleProps
  });
  const {
    wordsByLines: subtitleWordsByLine
  } = useText({
    children: subtitle,
    verticalAnchor: 'start',
    capHeight: subtitleFontSize,
    fontSize: subtitleFontSize,
    fontWeight: subtitleFontWeight,
    fontFamily: subtitleProps?.fontFamily,
    width: maxWidth,
    ...subtitleProps
  });
  const titleMeasuredWidth = titleWordsByLine.reduce((maxTitleWidth, line) => Math.max(maxTitleWidth, line.width ?? 0), 0);
  const subtitleMeasuredWidth = subtitleWordsByLine.reduce((maxSubtitleWidth, line) => Math.max(maxSubtitleWidth, line.width ?? 0), 0);
  const textMeasuredWidth = Math.ceil(Math.min(maxWidth, Math.max(titleMeasuredWidth, subtitleMeasuredWidth)));
  const measuredWidth = padding.right + padding.left + textMeasuredWidth;
  const width = propWidth ?? measuredWidth;
  const innerWidth = width - padding.left - padding.right;

  // offset container position based on horizontal + vertical anchor
  const horizontalAnchor = propsHorizontalAnchor || (Math.abs(dx) < Math.abs(dy) ? 'middle' : dx > 0 ? 'start' : 'end');
  const verticalAnchor = propsVerticalAnchor || (Math.abs(dx) > Math.abs(dy) ? 'middle' : dy > 0 ? 'start' : 'end');
  const containerCoords = useMemo(() => {
    let adjustedX = propsX == null ? x + dx : propsX;
    let adjustedY = propsY == null ? y + dy : propsY;
    if (horizontalAnchor === 'middle') adjustedX -= width / 2;
    if (horizontalAnchor === 'end') adjustedX -= width;
    if (verticalAnchor === 'middle') adjustedY -= height / 2;
    if (verticalAnchor === 'end') adjustedY -= height;
    return {
      x: adjustedX,
      y: adjustedY
    };
  }, [propsX, x, dx, propsY, y, dy, horizontalAnchor, verticalAnchor, width, height]);
  const titleFontFamily = titleProps?.fontFamily;
  const titleStyle = useMemo(() => ({
    fontSize: titleFontSize,
    fontWeight: titleFontWeight,
    fontFamily: titleFontFamily
  }), [titleFontSize, titleFontWeight, titleFontFamily]);
  const subtitleFontFamily = subtitleProps?.fontFamily;
  const subtitleStyle = useMemo(() => ({
    fontSize: subtitleFontSize,
    fontWeight: subtitleFontWeight,
    fontFamily: subtitleFontFamily
  }), [subtitleFontSize, subtitleFontWeight, subtitleFontFamily]);
  return !title && !subtitle ? null : /*#__PURE__*/_jsxs(Group, {
    top: containerCoords.y,
    left: containerCoords.x,
    pointerEvents: "none",
    className: cx('visx-annotationlabel', className),
    opacity: titleBounds.height === 0 && subtitleBounds.height === 0 ? 0 : 1,
    children: [showBackground && /*#__PURE__*/_jsx("rect", {
      className: "visx-annotationlabel-background",
      fill: backgroundFill,
      x: 0,
      y: 0,
      width: width,
      height: height,
      ...backgroundProps
    }), showAnchorLine && /*#__PURE__*/_jsx(AnchorLine, {
      anchorLineOrientation: Math.abs(dx) > Math.abs(dy) ? 'vertical' : 'horizontal',
      anchorLineStroke: anchorLineStroke,
      verticalAnchor: verticalAnchor,
      horizontalAnchor: horizontalAnchor,
      width: width,
      height: height
    }), title && /*#__PURE__*/_jsx(Text, {
      innerTextRef: titleRef,
      fill: fontColor,
      verticalAnchor: "start",
      x: padding.left + (titleProps?.textAnchor === 'middle' ? innerWidth / 2 : 0),
      y: padding.top,
      width: innerWidth,
      capHeight: titleFontSize // capHeight should match fontSize, used for first line line height
      ,
      style: titleStyle // used for size calculation
      ,
      ...titleProps,
      children: title
    }), subtitle && /*#__PURE__*/_jsx(Text, {
      innerTextRef: subtitleRef,
      fill: fontColor,
      verticalAnchor: "start",
      x: padding.left + (subtitleProps?.textAnchor === 'middle' ? innerWidth / 2 : 0),
      y: padding.top + (titleBounds.height ?? 0),
      dy: title ? subtitleDy : 0,
      width: innerWidth,
      capHeight: subtitleFontSize // capHeight should match fontSize, used for first line line height
      ,
      style: subtitleStyle // used for size calculation
      ,
      ...subtitleProps,
      children: subtitle
    })]
  });
}